import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useRefetchOnFocus } from "../../../hooks/use-refetch-on-focus.ts"
import {
  type OnboardingItem,
  type OnboardingItemId,
  type OnboardingReviewState,
  type TemplateSubsectionId,
  TEMPLATE_SUBSECTION_GUIDES,
  computeSetupProgress,
  isOnboardingItemComplete,
  nextIncompleteOnboardingItem,
  visibleOnboardingItems
} from "../content/onboarding.ts"
import { useGuideProgress } from "../hooks/use-guide-progress.ts"
import { useOnboardingSignals } from "../hooks/use-onboarding-signals.ts"
import { useCallbackRef } from "../../../hooks/use-callback-ref.ts"

const POLL_MS = 4000

// Every real path a Templates-step page can legitimately live under — the
// four areas' own routes are all under the first, and their own create/edit
// screens (currently just Document Templates') under the second.
const TEMPLATES_PATH_PREFIXES = ["/settings/templates", "/document_templates"]
// The settings drawer opens over whichever Communication tab is showing.
// A step may link to a query (the Communication settings drawer); pages match on the path.
const pathOf = (link: string) => link.split("?")[0]

const COMMUNICATION_SETTINGS_PREFIX = "/communication/"

interface GuideOnboardingAssistantContextValue {
  items: OnboardingItem[]
  currentItem: OnboardingItem | null
  activeItem: OnboardingItem | null
  /** 0-based position of `activeItem` within `items`, or -1 when no panel
   * is open — what "Step {{current}} of {{total}}" and the Back/Next
   * footer's enabled state both read. Wizard *position*, never completion. */
  activeIndex: number
  setupProgress: { completed: number; total: number }
  signals: ReturnType<typeof useOnboardingSignals>
  isItemComplete: (item: OnboardingItem) => boolean
  openStep: (id: OnboardingItemId) => void
  closeAssistant: () => void
  /** Sequential `activeIndex - 1` — a no-op at the first step. */
  back: () => void
  /** Sequential `activeIndex + 1` — never completes the current step, and
   * closes the assistant (not a forced completion) past the last step. */
  next: () => void
  markReviewed: (id: OnboardingItemId) => void
  skipStep: (id: OnboardingItemId) => void
  isGuideItemChecked: (subStepId: string) => boolean
  toggleGuideItem: (subStepId: string) => void
  /** Called by a real Settings page (currently only Permissions) after its
   * own save genuinely succeeds — advances to the next setup step, but only
   * when the assistant was actually open on that exact step; a save made
   * outside the guided flow is left alone, never yanked into it. */
  advanceAfterExternalSave: (id: OnboardingItemId) => void
  /** Non-null only while `activeItem.id === "templates"` and the user has
   * clicked one row's "View →" — the panel then shows that area's
   * contextual guide instead of the main Templates checklist, on top of a
   * real navigation to the area's own page. Nested *within* the Templates
   * step, not a 7th step: it never changes `activeIndex`/"Step X of 6". */
  templatesSubsection: TemplateSubsectionId | null
  openTemplatesSubsection: (id: TemplateSubsectionId) => void
  /** Marks the visited subsection's own checklist row reviewed (Level A —
   * "I looked at this", never a claim that something was created) and
   * returns to the main Templates checklist, on the real Templates hub
   * page — never just a bare page with the assistant gone. */
  backToTemplates: () => void
}

const GuideOnboardingAssistantContext =
  createContext<GuideOnboardingAssistantContextValue | null>(null)

/**
 * Mounted once, globally (CustomLayout), replacing both the old spotlight
 * onboarding tours and the global completion toast. Setup steps with real,
 * validatable state (`stepType: "complete"`) are only ever advanced by a
 * user reading real data as complete — never by clicking through; REVIEW
 * and OPTIONAL steps advance on an explicit "I've reviewed ... →" or
 * "Skip for now", recorded in GuideProgress, never invented from silence.
 */
export const GuideOnboardingAssistantProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)
  const userInfo = useAppSelector((state) => state.auth?.userInfo)
  const onGuideHome =
    location.pathname.replace(/\/+$/, "") === RoutesPaths.guide.home
  // Only Guide Home and an open setup step read the signals; once either has
  // asked, they stay loaded for the session.
  const [signalsWanted, setSignalsWanted] = useState(onGuideHome)
  const signals = useOnboardingSignals(userInfo?.id, signalsWanted)
  const {
    progress,
    setStepReviewed,
    setStepSkipped,
    isGuideItemChecked,
    setGuideItemChecked
  } = useGuideProgress()

  const [activeStepId, setActiveStepId] = useState<OnboardingItemId | null>(
    null
  )
  useEffect(() => {
    if (onGuideHome || activeStepId) setSignalsWanted(true)
  }, [onGuideHome, activeStepId])
  const [templatesSubsection, setTemplatesSubsection] =
    useState<TemplateSubsectionId | null>(null)

  const items = useMemo(
    () => visibleOnboardingItems(pages, codes),
    [pages, codes]
  )
  const review: OnboardingReviewState = useMemo(
    () => ({
      reviewedStepIds: progress.reviewedStepIds,
      skippedStepIds: progress.skippedStepIds
    }),
    [progress.reviewedStepIds, progress.skippedStepIds]
  )
  const refetchSignals = useCallbackRef(signals.refetch)

  const isItemComplete = useCallback(
    (item: OnboardingItem) =>
      isOnboardingItemComplete(item, signals, review, userInfo),
    [signals, review, userInfo]
  )

  const currentItem = nextIncompleteOnboardingItem(
    pages,
    codes,
    signals,
    review,
    userInfo
  )
  const setupProgress = computeSetupProgress(
    pages,
    codes,
    signals,
    review,
    userInfo
  )
  const activeIndex = activeStepId
    ? items.findIndex((item) => item.id === activeStepId)
    : -1
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null

  // `navigate()` returns before the destination's own unsaved-changes guard (`useBlocker`) decides whether to block it, so `activeStepId` can't switch until the URL confirms — else a blocked "Stay" would still close the assistant.
  const pendingStepIdRef = useRef<OnboardingItemId | null>(null)
  // Same URL-confirms-before-switching pattern as `pendingStepIdRef`, for entering/leaving a Templates subsection; one ref since only one such move is ever in flight.
  const pendingSubsectionRef = useRef<TemplateSubsectionId | "back" | null>(
    null
  )

  // Templates and Communication span several real pages each, so they're matched by prefix rather than an exact route — everything else has exactly one valid path, its own `linkTo`.
  const validPathsForItem = useCallback(
    (item: OnboardingItem, pathname: string): boolean => {
      if (item.id === "communication")
        return pathname.startsWith(COMMUNICATION_SETTINGS_PREFIX)
      if (item.id !== "templates") return pathname === pathOf(item.linkTo)
      return TEMPLATES_PATH_PREFIXES.some((prefix) =>
        pathname.startsWith(prefix)
      )
    },
    []
  )

  // The one path every navigation-triggering action (Back/Next/stepper/a sub-step) takes: go to the real page, mark it active, re-check its signal — and always reset an open Templates subsection so re-entering Step 6 shows the main checklist, never a stale contextual guide.
  const goToIndex = useCallback(
    (index: number) => {
      const item = items[Math.min(Math.max(index, 0), items.length - 1)]
      if (!item) return
      pendingSubsectionRef.current = null
      if (location.pathname !== pathOf(item.linkTo)) {
        pendingStepIdRef.current = item.id
        navigate(item.linkTo)
        setTemplatesSubsection(null)
        return
      }
      pendingStepIdRef.current = null
      setActiveStepId(item.id)
      setTemplatesSubsection(null)
      if (item.stepType === "complete") refetchSignals()
    },
    [items, navigate, location.pathname, refetchSignals]
  )

  const openStep = useCallback(
    (id: OnboardingItemId) => {
      const index = items.findIndex((existing) => existing.id === id)
      if (index === -1) return
      goToIndex(index)
    },
    [items, goToIndex]
  )

  const closeAssistant = useCallback(() => {
    pendingStepIdRef.current = null
    pendingSubsectionRef.current = null
    setActiveStepId(null)
    setTemplatesSubsection(null)
  }, [])

  const openTemplatesSubsection = useCallback(
    (id: TemplateSubsectionId) => {
      if (!activeItem || activeItem.id !== "templates") return
      const guide = TEMPLATE_SUBSECTION_GUIDES[id]
      if (location.pathname !== guide.route) {
        pendingSubsectionRef.current = id
        navigate(guide.route)
        return
      }
      pendingSubsectionRef.current = null
      setTemplatesSubsection(id)
    },
    [activeItem, navigate, location.pathname]
  )

  const backToTemplates = useCallback(() => {
    const templatesItem = items.find((item) => item.id === "templates")
    if (!templatesItem) return
    // "I looked at this" (Level A) — recorded the moment the user chooses
    // to leave the area, never on entry, and never a claim that a real
    // template was created there.
    if (templatesSubsection) {
      setGuideItemChecked(`templates.${templatesSubsection}`, true)
    }
    if (location.pathname !== pathOf(templatesItem.linkTo)) {
      pendingSubsectionRef.current = "back"
      navigate(templatesItem.linkTo)
      return
    }
    pendingSubsectionRef.current = null
    setTemplatesSubsection(null)
  }, [
    items,
    navigate,
    location.pathname,
    templatesSubsection,
    setGuideItemChecked
  ])

  // Confirms a pending navigation once the URL catches up, and separately closes the assistant on a real page mismatch (sidebar link, browser Back) — but never while this context's own pending navigation is still unresolved.
  useEffect(() => {
    const pendingId = pendingStepIdRef.current
    if (pendingId) {
      const pendingItem = items.find((item) => item.id === pendingId)
      if (pendingItem && location.pathname === pathOf(pendingItem.linkTo)) {
        pendingStepIdRef.current = null
        setActiveStepId(pendingId)
        if (pendingItem.stepType === "complete") signals.refetch()
        return
      }
      // Still on the current step's own page — the navigation is either
      // still in flight or was blocked and the user chose to stay; either
      // way, keep waiting rather than closing out from under them.
      if (activeItem && location.pathname === pathOf(activeItem.linkTo)) return
      // Neither the pending target nor the step still open — the blocked
      // attempt was abandoned some other way (a sidebar link, browser
      // back). Stop waiting for it and fall through to the normal check.
      pendingStepIdRef.current = null
    }

    const pendingSub = pendingSubsectionRef.current
    if (pendingSub) {
      if (pendingSub === "back") {
        const templatesItem = items.find((item) => item.id === "templates")
        if (
          templatesItem &&
          location.pathname === pathOf(templatesItem.linkTo)
        ) {
          pendingSubsectionRef.current = null
          setTemplatesSubsection(null)
          return
        }
      } else {
        const guide = TEMPLATE_SUBSECTION_GUIDES[pendingSub]
        if (location.pathname === guide.route) {
          pendingSubsectionRef.current = null
          setTemplatesSubsection(pendingSub)
          return
        }
      }
      // Blocked-and-stayed (or still in flight) on the subsection page this
      // move started from — keep waiting, same reasoning as the step-level
      // pending check above.
      if (
        templatesSubsection &&
        location.pathname ===
          TEMPLATE_SUBSECTION_GUIDES[templatesSubsection].route
      ) {
        return
      }
      pendingSubsectionRef.current = null
    }

    if (activeItem && !validPathsForItem(activeItem, location.pathname)) {
      setActiveStepId(null)
      setTemplatesSubsection(null)
    }
  }, [
    location.pathname,
    activeItem,
    items,
    signals,
    templatesSubsection,
    validPathsForItem
  ])

  // COMPLETE-type signals (Company, Team) are only fetched on mount/refetch
  // — poll while their real page is open and still incomplete so a real
  // save is detected without requiring a reload. Personal reads live off
  // already-loaded Redux state and never needs this.
  // Paused while the tab is hidden; checks once when it comes back.
  const pollSignals =
    !!activeItem &&
    activeItem.stepType === "complete" &&
    !!activeItem.signal &&
    !isItemComplete(activeItem)
  useRefetchOnFocus(refetchSignals, pollSignals, { pollIntervalMs: POLL_MS })

  // Sequential wizard position, never "next incomplete" — a step that's
  // still incomplete must still advance past when Next is clicked, or the
  // user is stuck reopening the same step forever.
  const back = useCallback(() => {
    if (activeIndex <= 0) return
    goToIndex(activeIndex - 1)
  }, [activeIndex, goToIndex])

  const next = useCallback(() => {
    if (activeIndex < 0) return
    if (activeIndex >= items.length - 1) {
      // "Finish Setup" — exits the guided flow, never a forced completion.
      closeAssistant()
      return
    }
    goToIndex(activeIndex + 1)
  }, [activeIndex, items.length, goToIndex, closeAssistant])

  // Reviewing/skipping the step the Setup Assistant currently has open
  // advances straight to the next step, same as clicking Next. Reviewing
  // or skipping from Guide Home's own current-step card (no panel open)
  // just records it and lets the page's own reactive current step move on
  // — a "Skip for now" click there stays on Guide Home, it doesn't yank the
  // user to a settings page they didn't ask to visit.
  const markReviewed = useCallback(
    (id: OnboardingItemId) => {
      setStepReviewed(id, true)
      if (activeStepId === id) next()
    },
    [setStepReviewed, next, activeStepId]
  )

  const skipStep = useCallback(
    (id: OnboardingItemId) => {
      setStepSkipped(id, true)
      if (activeStepId === id) next()
    },
    [setStepSkipped, next, activeStepId]
  )

  // Level A only (GuideProgress.checkedGuideItemIds) — a personal "I
  // reviewed/did this" mark, namespaced by step so the same sub-step id
  // (e.g. "review") can't collide across different steps. Never read by
  // isOnboardingItemComplete/computeSetupProgress.
  const toggleGuideItem = useCallback(
    (subStepId: string) => {
      if (!activeItem) return
      const key = `${activeItem.id}.${subStepId}`
      setGuideItemChecked(key, !isGuideItemChecked(key))
    },
    [activeItem, isGuideItemChecked, setGuideItemChecked]
  )

  const isGuideItemCheckedForActive = useCallback(
    (subStepId: string) =>
      activeItem ? isGuideItemChecked(`${activeItem.id}.${subStepId}`) : false,
    [activeItem, isGuideItemChecked]
  )

  const advanceAfterExternalSave = useCallback(
    (id: OnboardingItemId) => {
      if (activeStepId === id) next()
    },
    [activeStepId, next]
  )

  const value = useMemo<GuideOnboardingAssistantContextValue>(
    () => ({
      items,
      currentItem,
      activeItem,
      activeIndex,
      setupProgress,
      signals,
      isItemComplete,
      openStep,
      closeAssistant,
      back,
      next,
      markReviewed,
      skipStep,
      isGuideItemChecked: isGuideItemCheckedForActive,
      toggleGuideItem,
      advanceAfterExternalSave,
      templatesSubsection,
      openTemplatesSubsection,
      backToTemplates
    }),
    [
      items,
      currentItem,
      activeItem,
      activeIndex,
      setupProgress,
      signals,
      isItemComplete,
      openStep,
      closeAssistant,
      back,
      next,
      markReviewed,
      skipStep,
      isGuideItemCheckedForActive,
      toggleGuideItem,
      advanceAfterExternalSave,
      templatesSubsection,
      openTemplatesSubsection,
      backToTemplates
    ]
  )

  return (
    <GuideOnboardingAssistantContext.Provider value={value}>
      {children}
    </GuideOnboardingAssistantContext.Provider>
  )
}

// A safe no-op fallback rather than throwing, matching GuideTourContext's
// own precedent — a stray consumer rendered outside the provider (a test
// wrapper that doesn't need it) shouldn't crash the whole tree.
const NOOP: GuideOnboardingAssistantContextValue = {
  items: [],
  currentItem: null,
  activeItem: null,
  activeIndex: -1,
  setupProgress: { completed: 0, total: 0 },
  signals: {
    hasTeammates: null,
    hasProjectTypes: null,
    hasCompanyInfo: null,
    companyInfo: null,
    loaded: false,
    refetch: () => {}
  },
  isItemComplete: () => false,
  openStep: () => {},
  closeAssistant: () => {},
  back: () => {},
  next: () => {},
  markReviewed: () => {},
  skipStep: () => {},
  isGuideItemChecked: () => false,
  toggleGuideItem: () => {},
  advanceAfterExternalSave: () => {},
  templatesSubsection: null,
  openTemplatesSubsection: () => {},
  backToTemplates: () => {}
}

export const useGuideOnboardingAssistant = () =>
  useContext(GuideOnboardingAssistantContext) ?? NOOP
